import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Button, Center, Divider, Flex, HStack, Heading, Icon, Input, SimpleGrid,
  Spinner, Stat, StatHelpText, StatLabel, StatNumber, Table, Tbody, Td, Text, Th,
  Thead, Tr, VStack, Badge, Wrap, WrapItem,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalCloseButton,
  useDisclosure,
} from '@chakra-ui/react';
import {
  FiAnchor, FiCalendar, FiCheckCircle, FiClock, FiGift, FiMap, FiMapPin,
  FiRefreshCw, FiFilter, FiX, FiUsers,
} from 'react-icons/fi';
import { IconType } from 'react-icons';
import axiosInstance from '../../../api/AxiosInstance';

const COLOR_PRIMARY = '#5b182b';
const COLOR_DARK = '#691f30';

// ─────────── Utilidades ───────────
const fmtFecha = (v?: string) =>
  v ? new Date(v).toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';

const money = (v: any) =>
  v || v === 0 ? `$${Number(v).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—';

const enRango = (v: string | null | undefined, desde: string, hasta: string): boolean => {
  if (!desde && !hasta) return true;
  if (!v) return false;
  const t = new Date(v).getTime();
  if (isNaN(t)) return false;
  if (desde && t < new Date(`${desde}T00:00:00`).getTime()) return false;
  if (hasta && t > new Date(`${hasta}T23:59:59`).getTime()) return false;
  return true;
};

const EstatusBadge: React.FC<{ v?: string }> = ({ v }) => {
  const map: Record<string, string> = {
    activo: 'green', concluido: 'blue', inactivo: 'gray',
    autorizado: 'green', pendiente: 'yellow', rechazado: 'red',
  };
  return (
    <Badge colorScheme={map[(v || '').toLowerCase()] || 'gray'} fontSize="2xs" px={2} py={0.5} borderRadius="full">
      {v || '—'}
    </Badge>
  );
};

// ─────────── Tipos ───────────
interface Column {
  header: string;
  cell: (row: any) => React.ReactNode;
  isNumeric?: boolean;
}

interface Tarjeta {
  key: string;
  label: string;
  value: number | string;
  help: string;
  icon: IconType;
  color: string;
  rows: any[];
  columns: Column[];
  emptyText: string;
}

// ─────────── Tarjeta KPI (drill-down) ───────────
const TarjetaKPI: React.FC<{ t: Tarjeta; onClick: () => void }> = ({ t, onClick }) => (
  <Box
    bg="white"
    borderRadius="lg"
    boxShadow="sm"
    borderLeft={`4px solid ${t.color}`}
    p={4}
    cursor="pointer"
    role="button"
    tabIndex={0}
    onClick={onClick}
    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onClick(); }}
    transition="all 0.15s ease"
    _hover={{ boxShadow: 'lg', transform: 'translateY(-3px)' }}
    _active={{ transform: 'translateY(-1px)' }}
  >
    <HStack justify="space-between">
      <Stat>
        <StatLabel color="gray.500" fontSize="xs">{t.label}</StatLabel>
        <StatNumber color={t.color} fontSize="2xl">{t.value}</StatNumber>
        <StatHelpText fontSize="xs" mb={0}>{t.help}</StatHelpText>
      </Stat>
      <Icon as={t.icon} boxSize={9} color={t.color} opacity={0.45} />
    </HStack>
  </Box>
);

// ─────────── Columnas por tipo ───────────
const colsAtractivos: Column[] = [
  { header: 'Nombre', cell: (r) => <Text noOfLines={1} maxW="240px" title={r.nombre}>{r.nombre}</Text> },
  { header: 'Tipo', cell: (r) => r.tipo || '—' },
  { header: 'Comunidad', cell: (r) => r.comunidad || '—' },
  { header: 'Costo', cell: (r) => r.costo || '—' },
  { header: 'Estatus', cell: (r) => <EstatusBadge v={r.estatus} /> },
];

const colsEventos: Column[] = [
  { header: 'Título', cell: (r) => <Text noOfLines={1} maxW="220px" title={r.titulo}>{r.titulo}</Text> },
  { header: 'Tipo', cell: (r) => r.tipo || '—' },
  { header: 'Comunidad', cell: (r) => r.comunidad || '—' },
  { header: 'Fecha', cell: (r) => fmtFecha(r.fecha) },
  { header: 'Visitantes', cell: (r) => (r.visitantes ?? 0).toLocaleString('es-MX'), isNumeric: true },
  { header: 'Estatus', cell: (r) => <EstatusBadge v={r.estatus} /> },
];

const colsArtesanos: Column[] = [
  { header: 'Nombre', cell: (r) => `${r.nombres || ''} ${r.apellidos || ''}`.trim() || '—' },
  { header: 'Artesanía', cell: (r) => r.tipoArtesania || '—' },
  { header: 'Localidad', cell: (r) => r.localidad || '—' },
  { header: 'Teléfono', cell: (r) => r.telefono || '—' },
  { header: 'Estatus', cell: (r) => <EstatusBadge v={r.estatus} /> },
];

const colsRutas: Column[] = [
  { header: 'Nombre', cell: (r) => <Text noOfLines={1} maxW="220px" title={r.nombre}>{r.nombre}</Text> },
  { header: 'Tipo', cell: (r) => r.tipo || '—' },
  { header: 'Duración', cell: (r) => r.duracion || '—' },
  { header: 'Costo', cell: (r) => money(r.costo) },
  { header: 'Cupo', cell: (r) => r.cupo ?? '—', isNumeric: true },
  { header: 'Estatus', cell: (r) => <EstatusBadge v={r.estatus} /> },
];

const colsApoyos: Column[] = [
  { header: 'Beneficiario', cell: (r) => r.beneficiario ? `${r.beneficiario.nombres || ''} ${r.beneficiario.apellidos || ''}`.trim() : '—' },
  { header: 'Tipo', cell: (r) => r.tipo || '—' },
  { header: 'Monto', cell: (r) => money(r.monto), isNumeric: true },
  { header: 'Fecha solicitud', cell: (r) => fmtFecha(r.fechaSolicitud) },
  { header: 'Estatus', cell: (r) => <EstatusBadge v={r.estatus} /> },
];

// ─────────── Componente principal ───────────
const DashboardTurismo: React.FC = () => {
  const [cargando, setCargando] = useState(true);

  // Datos crudos (se cargan una sola vez; el filtro de fecha es client-side)
  const [atractivos, setAtractivos] = useState<any[]>([]);
  const [eventos, setEventos] = useState<any[]>([]);
  const [artesanos, setArtesanos] = useState<any[]>([]);
  const [rutas, setRutas] = useState<any[]>([]);
  const [apoyos, setApoyos] = useState<any[]>([]);

  // Filtro de fecha: valores en edición vs. aplicados
  const [desdeInput, setDesdeInput] = useState('');
  const [hastaInput, setHastaInput] = useState('');
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [seleccion, setSeleccion] = useState<Tarjeta | null>(null);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const [atr, evt, art, rut, apo] = await Promise.all([
        axiosInstance.get('/turismo/atractivos').then((r) => r.data).catch(() => []),
        axiosInstance.get('/turismo/eventos').then((r) => r.data).catch(() => []),
        axiosInstance.get('/turismo/artesanos').then((r) => r.data).catch(() => []),
        axiosInstance.get('/turismo/rutas').then((r) => r.data).catch(() => []),
        axiosInstance.get('/turismo/apoyos').then((r) => r.data).catch(() => []),
      ]);
      setAtractivos(Array.isArray(atr) ? atr : []);
      setEventos(Array.isArray(evt) ? evt : []);
      setArtesanos(Array.isArray(art) ? art : []);
      setRutas(Array.isArray(rut) ? rut : []);
      setApoyos(Array.isArray(apo) ? apo : []);
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const aplicar = () => { setDesde(desdeInput); setHasta(hastaInput); };
  const limpiar = () => { setDesdeInput(''); setHastaInput(''); setDesde(''); setHasta(''); };
  const hayFiltro = Boolean(desde || hasta);

  // ─────────── Cálculos derivados (recalculan al aplicar el rango) ───────────
  // Atractivos, artesanos, rutas → filtro por createdAt.  Eventos → por fecha del evento.  Apoyos → por fechaSolicitud.
  const atractivosActivos = useMemo(
    () => atractivos.filter((a) => a.estatus === 'activo' && enRango(a.createdAt, desde, hasta)),
    [atractivos, desde, hasta],
  );
  const eventosRango = useMemo(
    () => eventos.filter((e) => enRango(e.fecha, desde, hasta)),
    [eventos, desde, hasta],
  );
  const eventosConcluidos = useMemo(
    () => eventosRango.filter((e) => e.estatus === 'concluido'),
    [eventosRango],
  );
  const artesanosRango = useMemo(
    () => artesanos.filter((a) => enRango(a.createdAt, desde, hasta)),
    [artesanos, desde, hasta],
  );
  const rutasActivas = useMemo(
    () => rutas.filter((r) => r.estatus === 'activo' && enRango(r.createdAt, desde, hasta)),
    [rutas, desde, hasta],
  );
  const apoyosRango = useMemo(
    () => apoyos.filter((a) => enRango(a.fechaSolicitud, desde, hasta)),
    [apoyos, desde, hasta],
  );
  const apoyosAutorizados = useMemo(
    () => apoyosRango.filter((a) => a.estatus === 'autorizado'),
    [apoyosRango],
  );
  const apoyosPendientes = useMemo(
    () => apoyosRango.filter((a) => a.estatus === 'pendiente'),
    [apoyosRango],
  );

  const visitantesTotal = useMemo(
    () => eventosRango.reduce((s, e) => s + (Number(e.visitantes) || 0), 0),
    [eventosRango],
  );
  const derramaTotal = useMemo(
    () => apoyosAutorizados.reduce((s, a) => s + (Number(a.monto) || 0), 0),
    [apoyosAutorizados],
  );

  const tarjetas: Tarjeta[] = useMemo(() => [
    {
      key: 'atractivos', label: 'Atractivos turísticos', value: atractivosActivos.length,
      help: hayFiltro ? 'Activos en el rango' : `${atractivos.length} registrados`,
      icon: FiMapPin, color: COLOR_PRIMARY, rows: atractivosActivos, columns: colsAtractivos,
      emptyText: 'No hay atractivos activos en el rango.',
    },
    {
      key: 'eventos', label: 'Eventos turísticos', value: eventosRango.length,
      help: `${visitantesTotal.toLocaleString('es-MX')} visitantes`,
      icon: FiCalendar, color: '#2B6CB0', rows: eventosRango, columns: colsEventos,
      emptyText: 'No hay eventos en el rango.',
    },
    {
      key: 'eventosConcluidos', label: 'Eventos concluidos', value: eventosConcluidos.length,
      help: 'Estatus: concluido',
      icon: FiCheckCircle, color: '#2F855A', rows: eventosConcluidos, columns: colsEventos,
      emptyText: 'No hay eventos concluidos en el rango.',
    },
    {
      key: 'artesanos', label: 'Artesanos registrados', value: artesanosRango.length,
      help: hayFiltro ? 'Altas en el rango' : 'Padrón artesanal',
      icon: FiUsers, color: '#bb8c52', rows: artesanosRango, columns: colsArtesanos,
      emptyText: 'No hay artesanos en el rango.',
    },
    {
      key: 'rutas', label: 'Rutas turísticas', value: rutasActivas.length,
      help: hayFiltro ? 'Activas en el rango' : `${rutas.length} registradas`,
      icon: FiMap, color: '#805AD5', rows: rutasActivas, columns: colsRutas,
      emptyText: 'No hay rutas activas en el rango.',
    },
    {
      key: 'apoyosAut', label: 'Apoyos autorizados', value: apoyosAutorizados.length,
      help: `${money(derramaTotal)} otorgados`,
      icon: FiGift, color: '#2F855A', rows: apoyosAutorizados, columns: colsApoyos,
      emptyText: 'No hay apoyos autorizados en el rango.',
    },
    {
      key: 'apoyosPend', label: 'Apoyos pendientes', value: apoyosPendientes.length,
      help: 'Por revisar',
      icon: FiClock, color: '#B7791F', rows: apoyosPendientes, columns: colsApoyos,
      emptyText: 'No hay apoyos pendientes en el rango.',
    },
  ], [
    atractivos, atractivosActivos, eventosRango, eventosConcluidos, artesanosRango,
    rutas, rutasActivas, apoyosAutorizados, apoyosPendientes, visitantesTotal, derramaTotal, hayFiltro,
  ]);

  const abrirDetalle = (t: Tarjeta) => { setSeleccion(t); onOpen(); };

  const rangoTexto = useMemo(() => {
    if (!desde && !hasta) return 'Todos los registros';
    return `${desde ? fmtFecha(desde) : 'inicio'} — ${hasta ? fmtFecha(hasta) : 'hoy'}`;
  }, [desde, hasta]);

  return (
    <VStack spacing={5} align="stretch" px={{ base: 2, md: 4 }} pt={79} pb={6}>
      {/* Header */}
      <Box bg="white" borderRadius="lg" p={5} boxShadow="sm" borderLeft={`4px solid ${COLOR_PRIMARY}`}>
        <Flex align="center" justify="space-between" flexWrap="wrap" gap={3}>
          <HStack spacing={3}>
            <Icon as={FiAnchor} boxSize={7} color={COLOR_PRIMARY} />
            <Box>
              <Heading size="md" color={COLOR_PRIMARY}>Dashboard de Turismo y Fomento Artesanal</Heading>
              <Text fontSize="sm" color="gray.500" mt={1}>
                Indicadores del área de turismo municipal · Da clic en un indicador para ver el detalle
              </Text>
            </Box>
          </HStack>
          <Button size="sm" leftIcon={<FiRefreshCw />} variant="outline" colorScheme="red"
            onClick={cargar} isLoading={cargando}>
            Actualizar
          </Button>
        </Flex>
      </Box>

      {/* Filtro de fecha */}
      <Box bg="white" borderRadius="lg" boxShadow="sm" p={4} borderTop={`3px solid ${COLOR_PRIMARY}`}>
        <Wrap spacing={4} align="flex-end">
          <WrapItem>
            <HStack spacing={2} color={COLOR_PRIMARY}>
              <Icon as={FiFilter} />
              <Text fontWeight="bold" fontSize="sm">Filtrar por fecha</Text>
            </HStack>
          </WrapItem>
          <WrapItem>
            <Box>
              <Text fontSize="xs" color="gray.500" fontWeight="bold" mb={1}>Desde</Text>
              <Input type="date" size="sm" value={desdeInput} maxW="170px"
                onChange={(e) => setDesdeInput(e.target.value)} focusBorderColor={COLOR_PRIMARY} />
            </Box>
          </WrapItem>
          <WrapItem>
            <Box>
              <Text fontSize="xs" color="gray.500" fontWeight="bold" mb={1}>Hasta</Text>
              <Input type="date" size="sm" value={hastaInput} maxW="170px"
                onChange={(e) => setHastaInput(e.target.value)} focusBorderColor={COLOR_PRIMARY} />
            </Box>
          </WrapItem>
          <WrapItem>
            <HStack spacing={2}>
              <Button size="sm" color="white" bg={COLOR_PRIMARY} _hover={{ bg: COLOR_DARK }}
                leftIcon={<FiFilter />} onClick={aplicar}>
                Aplicar
              </Button>
              <Button size="sm" variant="outline" color={COLOR_PRIMARY} borderColor={COLOR_PRIMARY}
                _hover={{ bg: 'gray.50' }} leftIcon={<FiX />} onClick={limpiar}
                isDisabled={!hayFiltro && !desdeInput && !hastaInput}>
                Limpiar
              </Button>
            </HStack>
          </WrapItem>
          <WrapItem>
            <Badge bg={hayFiltro ? COLOR_PRIMARY : 'gray.200'} color={hayFiltro ? 'white' : 'gray.600'}
              px={2} py={1} borderRadius="md" fontSize="2xs">
              Periodo: {rangoTexto}
            </Badge>
          </WrapItem>
        </Wrap>
      </Box>

      <Divider />

      {/* KPIs interactivos */}
      {cargando ? (
        <Center py={16}><Spinner size="xl" color={COLOR_PRIMARY} thickness="3px" /></Center>
      ) : (
        <SimpleGrid columns={{ base: 1, sm: 2, lg: 3, xl: 4 }} spacing={4}>
          {tarjetas.map((t) => <TarjetaKPI key={t.key} t={t} onClick={() => abrirDetalle(t)} />)}
        </SimpleGrid>
      )}

      {/* Modal drill-down */}
      <Modal isOpen={isOpen} onClose={onClose} size="4xl" scrollBehavior="inside" isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader bg={COLOR_PRIMARY} color="white" borderTopRadius="md" py={3}>
            <HStack spacing={2}>
              {seleccion && <Icon as={seleccion.icon} />}
              <Text fontSize="md">{seleccion?.label}</Text>
              {seleccion && (
                <Badge bg="white" color={COLOR_PRIMARY} borderRadius="full" px={2}>
                  {seleccion.rows.length}
                </Badge>
              )}
              <Text fontSize="xs" opacity={0.85} fontWeight="normal">· {rangoTexto}</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton color="white" />
          <ModalBody py={4}>
            {seleccion && (
              seleccion.rows.length === 0 ? (
                <Flex direction="column" align="center" py={10} color="gray.400">
                  <Icon as={seleccion.icon} boxSize={10} mb={2} />
                  <Text fontSize="sm">{seleccion.emptyText}</Text>
                </Flex>
              ) : (
                <Box overflowX="auto" borderRadius="md" boxShadow="sm">
                  <Table variant="simple" size="sm">
                    <Thead bg={COLOR_PRIMARY}>
                      <Tr>
                        {seleccion.columns.map((c) => (
                          <Th key={c.header} color="white" fontSize="xs" isNumeric={c.isNumeric}>{c.header}</Th>
                        ))}
                      </Tr>
                    </Thead>
                    <Tbody>
                      {seleccion.rows.map((row, idx) => (
                        <Tr key={row.id ?? idx} bg={idx % 2 === 0 ? 'white' : 'gray.50'} _hover={{ bg: '#f7efe8' }}>
                          {seleccion.columns.map((c) => (
                            <Td key={c.header} fontSize="sm" isNumeric={c.isNumeric}>{c.cell(row)}</Td>
                          ))}
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
              )
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </VStack>
  );
};

export default DashboardTurismo;
